import React, {
  Fragment,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Bell, Inbox, MessageCircle, MessageSquare } from 'react-feather';
import { useLocation } from 'react-router-dom';
import Arrow from '@assets/images/dashboard/arrow.svg';
import List from '@assets/images/dashboard/list.svg';
import LogOut from '@assets/images/dashboard/logout.svg';
import OpenDoor from '@assets/images/dashboard/opendoor.svg';
import LogoLetmeup from '@assets/images/Logo_letmeupKorean.svg';
import LogoLetmeupMark from '@assets/images/Logo_letmeupMark.svg';
import User from '@assets/images/UserCircle.svg';
import ChannelService from '@components/dashboard/channelTalk';
import { announcementUnreadCount } from '@lib/api';
import { deleteLogout } from '@lib/api/v2/auth';
import { getChatRooms } from '@lib/api/v2/chat';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import useKioskOption from '@lib/hooks/useKioskOption';
import { openDoor } from '@lib/socket';
import useAppStore from '@store/useAppStore';
import { media } from '@styles/mediaQuery';
import { getAllToken, removeToken } from '@util/token';
import styled, { css } from 'styled-components';

const HeaderNoticeModal = lazy(() => import('@components/modal/HeaderNoticeModal'));
const SendOpinionModal = lazy(() => import('@components/modal/SendOpinionModal'));
const SocketControlModal = lazy(() => import('@components/modal/SocketControlModal'));

const CHANNEL_TALK_PLUGIN_KEY = 'c7244504-2def-4fd9-b399-3336a3cbcceb';

const PAGE_TITLES = [
  { prefix: '/admin/dashboard', title: '대시보드' },
  { prefix: '/backoffice', title: '통합 관리' },
  { prefix: '/seat', title: '좌석 및 사물함' },
  { prefix: '/locker', title: '좌석 및 사물함' },
  { prefix: '/exit-data', title: '지점 출입 조회' },
  { prefix: '/ticket', title: '입장권 관리' },
  { prefix: '/pay', title: '결제정보 조회' },
  { prefix: '/sales', title: '매출 분석' },
  { prefix: '/seminar', title: '세미나실 예약' },
  { prefix: '/user', title: '회원 관리' },
  { prefix: '/message', title: '메시지 관리' },
  { prefix: '/chat', title: '채팅' },
  { prefix: '/mileage', title: '마일리지 관리' },
  { prefix: '/coupon', title: '쿠폰 관리' },
  { prefix: '/product', title: '상품 관리' },
  { prefix: '/setting', title: '설정' },
  { prefix: '/subscription', title: '구독 관리' },
  { prefix: '/app', title: '렛미업 앱 관리' },
  { prefix: '/dashboard', title: '내 이용 현황' },
];

const Divider = styled.div`
  width: 90%;
  background: ${({ theme }) => theme.neutralGray[200]};
  height: 1px;
`;

const ToggleMenu = styled.div`
  position: fixed;

  top: ${({ $anchor }) => ($anchor ? `${$anchor.top}px` : '3.5rem')};
  right: ${({ $anchor }) => ($anchor ? 'auto' : '1rem')};
  left: ${({ $anchor }) => ($anchor ? `${$anchor.left}px` : 'auto')};

  display: flex;
  flex-direction: column;

  width: 14rem;

  border-radius: 1rem;
  overflow: hidden;
  z-index: 450;
  border: 1px solid ${({ theme }) => theme.neutralGray[200]};
  background: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.shadow.md};

  transition: opacity 0.15s ease-in-out, transform 0.2s ease-in-out,
    visibility 0.15s ease-in-out;

  .list-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem;
  }

  ${({ enabled }) =>
    enabled
      ? css`
          visibility: visible;
          pointer-events: auto;
          opacity: 1;
          transform: translateY(0);
        `
      : css`
          z-index: -1;
          visibility: hidden;
          pointer-events: none;
          opacity: 0;
          transform: translateY(-0.5rem);
        `}
`;

const MenuListItem = styled.button.attrs({ type: 'button' })`
  display: grid;
  grid-template-columns: 2rem auto;
  align-items: center;
  padding: 0.75rem;
  border: 0;
  background: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 1rem;
  border-radius: 0.5rem;
  width: 100%;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.main[100]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: -2px;
  }
`;

const DesktopShortcutNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 0 0 auto;
`;

const DesktopShortcutButton = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 2.5rem;
  gap: 0.4rem;
  padding: 0.55rem 0.7rem;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: rgba(24, 31, 43, 0.77);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.darkNavy};
    background: ${({ theme }) => theme.main[100]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: -2px;
  }

  > svg {
    width: 1rem;
    height: 1rem;
  }
`;

const HeaderCount = styled.span`
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: #d92d20;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
`;

const MenuLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
`;

const StyledMenuHandler = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 24rem;
  min-height: 2.5rem;
  border: 1px solid ${({ theme }) => theme.neutralGray[200]};
  border-radius: ${({ theme }) => theme.radius.full};
  padding: 0.45rem 0.75rem;
  background: ${({ theme }) => theme.white};
  color: rgba(24, 31, 43, 0.77);
  cursor: pointer;

  .merchant-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.875rem;
    font-weight: 700;
  }

  &:hover {
    border-color: ${({ theme }) => theme.main[300]};
    background: ${({ theme }) => theme.main[100]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 2px;
  }

  @media (min-width: 1280px) {
    min-height: 2.5rem;
    max-width: 22rem;
    padding: 0.45rem 0.55rem;
    border-color: transparent;
    border-radius: 0.65rem;

    .merchant-name {
      color: ${({ theme }) => theme.darkNavy};
      font-size: 1rem;
      font-weight: 700;
    }
  }
`;

const ResponseMerchantName = styled.div`
  color: #2b2b2b;
  font-size: 16px;
  font-weight: 600;
  ${media.desktop`
    padding: 1rem;
    background-color: #f1f3f6;
    display: flex;
    color: #686c71;
    word-break: keep-all;
    > div {
      &:first-child {
        margin-right: 0.5rem;
      }
    }
  `}
`;

const DesktopPageContext = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '';
    width: 1px;
    height: 1rem;
    background: ${({ theme }) => theme.neutralGray[200]};
  }

  strong {
    color: ${({ theme }) => theme.neutralGray[400]};
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.3;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 0.75rem;

  .nav-right {
    flex: 0 0 0 !important;
    width: 0 !important;
    max-width: 0 !important;
    min-width: 0;
    margin-right: 0 !important;
    overflow: visible;
  }
  .nav-menus {
    width: 0;
    margin-right: 0 !important;
    margin-bottom: 0;
    padding-left: 0;
    .media-body {
      padding: 0.5rem;
      &:hover {
        background-color: #f1f3f6;
        border-radius: 0.41vw;
      }
    }
  }
  .profile-media img {
    width: 0.52vw !important;
    height: 0.31vw !important;
    margin-left: 0.2vw;
  }
  .profile-dropdown {
    width: 14rem;
    /* border-radius: 0.83vw !important; */
    border-radius: 1rem;
    padding: 0.41vw 0.41vw !important;
    box-shadow: 0px 0px 24px 0px #00000029;
    right: 1rem;
    /* top: 2.9vw !important; */
    /* right: 0 !important; */
    /* left: 1.53vw !important; */
    li {
      justify-content: flex-start;
      padding: 0.62vw 1.45vw;
      img {
        width: 0.93vw;
        margin-right: 0.41vw;
      }
      span {
        font-size: 0.93vw;
        font-weight: 600;
        color: #686c71 !important;
      }
      &:first-child {
        border-top: none !important;
      }
      &:hover {
        background-color: #f1f3f6;
        border-radius: 0.41vw;
      }
    }
    hr {
      margin: 0;
      width: 80%;
      border-top: 1px solid #dde4eb;
      margin-left: 10%;
    }
  }
  ${media.tablet`
    .profile-dropdown {
      position: fixed;
      border-radius: 4.44vw !important;
      padding: 0 !important;

      right: 1rem;
      li {
        padding: 4.72vw 8.88vw !important;

        img {
          width: 5vw;
          margin-right: 2.22vw;
        }
        span {
          font-size: 5vw;
        }
        &:nth-child(2) {
          border-top: none !important;
        }
        &:hover {
          background-color: #ffffff;
          border-radius: 0.41vw;
        }
      }
      hr {
        margin-left: 6.66vw;
      }
    }
  `}
`;

const MobileHeader = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .mobile-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .mobile-menu-button {
    margin-right: 1rem;
  }
  .logo {
    height: 1.5rem;
  }
  .user {
    width: 1.75rem;
  }

  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
    gap: 0.5rem;

    .mobile-left {
      grid-column: 1;
      justify-self: start;
    }

    .mobile-menu-button {
      margin-right: 0;
    }

    .mobile-right {
      grid-column: 3;
      justify-self: end;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const MobilePageTitle = styled.button`
  grid-column: 2;
  width: 100%;
  min-height: 2.75rem;
  min-width: 0;
  overflow: hidden;
  padding: 0.5rem 0.25rem;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.darkNavy};
  font: inherit;
  font-size: 1.0625rem;
  font-weight: 750;
  line-height: 1.15;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.main[100]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: -2px;
  }
`;

const MobileAccountButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.main[200]};
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.white};
  cursor: pointer;

  img {
    width: 1.5rem;
    height: auto;
  }

  &:hover {
    background: ${({ theme }) => theme.main[100]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 2px;
  }
`;

const HamburgerButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.45rem;
  border: 0;
  border-radius: 0.75rem;
  background: ${({ $open, theme }) => ($open ? theme.main[100] : 'transparent')};
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  span {
    display: block;
    width: 1.45rem;
    height: 0.125rem;
    border-radius: 999px;
    background: ${({ theme }) => theme.darkNavy};
    transform-origin: center;
    transition: transform 0.22s ease, opacity 0.18s ease, width 0.22s ease;
  }

  span:nth-child(1) {
    transform: ${({ $open }) => ($open ? 'translateY(0.425rem) rotate(45deg)' : 'none')};
  }

  span:nth-child(2) {
    opacity: ${({ $open }) => ($open ? 0 : 1)};
    width: ${({ $open }) => ($open ? '0' : '1.45rem')};
  }

  span:nth-child(3) {
    transform: ${({ $open }) => ($open ? 'translateY(-0.425rem) rotate(-45deg)' : 'none')};
  }

  &:active {
    transform: scale(0.96);
  }
`;

const Rightbar = (props) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const location = useLocation();

  const { merchantData, userPhoneNumber } = useAppStore();
  const {
    kioskOptions,
    usingLegacyMultiKiosk,
    usingMultiKiosk,
    legacyMultiKioskFloor,
    handleOpenDoor,
    handleOpenDoorLegacy,
  } = useKioskOption();

  const [toggleMenuEnabled, setToggleMenuEnabled] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isDoorModal, setIsDoorModal] = useState(false);
  const [isNoticeModal, setIsNoticeModal] = useState(false);
  const [isOpinionModal, setIsOpinionModal] = useState(false);
  const [name, setName] = useState('');
  const [merchantCode, setMerchantCode] = useState('');
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadNoticeCount, setUnreadNoticeCount] = useState(0);
  const menuHandlerRef = useRef(null);

  const { screenType } = props;
  const pageTitle = useMemo(
    () =>
      PAGE_TITLES.find(({ prefix }) => location.pathname.startsWith(prefix))?.title ||
      '매장 운영',
    [location.pathname],
  );

  const { adminToken, userToken, merchantToken } = getAllToken();

  const handleLogout = async () => {
    try {
      if (!props) return;
      await createRequest(deleteLogout);
    } catch (error) {
      catchError(error);
    }
  };

  const updateMenuAnchor = useCallback(() => {
    const trigger = menuHandlerRef.current;
    if (!trigger || screenType === 'mobile') {
      setMenuAnchor(null);
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const menuWidth = 14 * 16;
    const edgeGap = 16;
    setMenuAnchor({
      top: rect.bottom + 8,
      left: Math.min(Math.max(edgeGap, rect.left), window.innerWidth - menuWidth - edgeGap),
    });
  }, [screenType]);

  const handleClick = useCallback(() => {
    setToggleMenuEnabled((enabled) => {
      if (!enabled) updateMenuAnchor();
      return !enabled;
    });
  }, [updateMenuAnchor]);

  useEffect(() => {
    if (!toggleMenuEnabled || screenType === 'mobile') return undefined;

    updateMenuAnchor();
    window.addEventListener('resize', updateMenuAnchor);
    return () => window.removeEventListener('resize', updateMenuAnchor);
  }, [screenType, toggleMenuEnabled, updateMenuAnchor]);

  const goToMerchantList = () => {
    removeToken(['merchantToken', 'merchantRefreshToken']);
    if (UserType.merchantUserV2) {
      localStorage.setItem('level', 'user');
      window.location.href = `${process.env.PUBLIC_URL}/select-merchant`;
      return;
    }
    localStorage.setItem('level', 'admin');
    window.location.href = `${process.env.PUBLIC_URL}/backoffice/merchant/list`;
  };

  const UserType = useMemo(() => {
    return {
      adminMerchant: !!merchantToken && !!adminToken,
      merchantUserV1: !!merchantToken && !userToken && !!userPhoneNumber,
      merchantUserV2: !!merchantToken && !!userToken,
      userV2: !!userToken && !merchantToken,
      merchant: !!merchantToken && !userToken,
      admin: adminToken && !merchantToken,
    };
  }, [userToken, merchantToken, adminToken, userPhoneNumber]);

  const isMerchantContext =
    UserType.merchant ||
    UserType.merchantUserV1 ||
    UserType.merchantUserV2 ||
    UserType.adminMerchant;
  const canUseHeaderActions = isMerchantContext || UserType.admin;
  const canUseChannelTalk = isMerchantContext || UserType.admin;

  const openNotice = useCallback(() => {
    setToggleMenuEnabled(false);
    if (UserType.admin) {
      window.location.href = `${process.env.PUBLIC_URL}/backoffice/merchant/notice`;
      return;
    }
    setIsNoticeModal(true);
  }, [UserType.admin]);

  const openOpinion = useCallback(() => {
    setToggleMenuEnabled(false);
    if (UserType.admin) {
      window.location.href = `${process.env.PUBLIC_URL}/backoffice/merchant/opinion`;
      return;
    }
    setIsOpinionModal(true);
  }, [UserType.admin]);

  const openChannelTalk = useCallback(() => {
    setToggleMenuEnabled(false);
    ChannelService.toggleMessenger();
  }, []);

  const openCustomerChat = useCallback(() => {
    setToggleMenuEnabled(false);
    window.location.href = UserType.admin
      ? `${process.env.PUBLIC_URL}/backoffice/chat`
      : `${process.env.PUBLIC_URL}/chat/inbox`;
  }, [UserType.admin]);

  useEffect(() => {
    if (!isMerchantContext && !UserType.admin) return undefined;
    let active = true;
    const fetchUnreadChats = async () => {
      try {
        const response = await getChatRooms(Boolean(UserType.admin), {
          status: 'open',
          limit: 100,
        });
        const rooms = response?.data?.data?.rooms || [];
        const count = rooms.reduce((sum, room) => sum + Number(room.unreadStaffCount || 0), 0);
        if (active) setUnreadChatCount(count);
      } catch (error) {
        if (active) setUnreadChatCount(0);
      }
    };
    fetchUnreadChats();
    const timer = window.setInterval(fetchUnreadChats, 30000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [isMerchantContext, UserType.admin]);

  // 미확인 공지 수. 본사 계정은 백오피스 공지 관리로 이동하므로 지점 컨텍스트에서만 조회한다.
  const fetchUnreadNotices = useCallback(async () => {
    if (!isMerchantContext) return;
    try {
      const response = await announcementUnreadCount();
      setUnreadNoticeCount(Number(response?.data?.unreadCount || 0));
    } catch (error) {
      setUnreadNoticeCount(0);
    }
  }, [isMerchantContext]);

  useEffect(() => {
    if (!isMerchantContext) return undefined;
    fetchUnreadNotices();
    // 공지는 채팅만큼 자주 바뀌지 않아 5분 간격으로 확인한다.
    const timer = window.setInterval(fetchUnreadNotices, 300000);
    return () => window.clearInterval(timer);
  }, [isMerchantContext, fetchUnreadNotices]);

  const MenuHandler = useMemo(() => {
    // return (
    //   <div className="media profile-media" style={{ alignItems: 'center', width: 'max-content' }}>
    //     <div>
    //       <div style={{ display: 'flex' }} className="media-body" onClick={() => handleClick()}>
    //         <span>
    //           {name ? name : ''}{' '}
    //           {merchantCode && (
    //             <>
    //               <small>({merchantCode})</small>
    //             </>
    //           )}
    //         </span>
    //         <span>
    //           <img src={Arrow} alt="더보기" />
    //           {/* <i style={{ marginLeft: "15px" }} className="middle fa fa-angle-down"></i> */}
    //         </span>
    //       </div>
    //     </div>
    //   </div>
    // );
    return (
      <StyledMenuHandler
        ref={menuHandlerRef}
        type="button"
        className="merchant-menu-handler"
        aria-label="지점 및 계정 메뉴"
        aria-controls="merchant-account-menu"
        aria-expanded={toggleMenuEnabled}
        onClick={handleClick}>
        <span className="merchant-name">{name || ''}</span>
        <span>{merchantCode && <>({merchantCode})</>}</span>
        <span>
          <img src={Arrow} alt="더보기" />
        </span>
      </StyledMenuHandler>
    );
  }, [handleClick, name, merchantCode, toggleMenuEnabled]);

  const moveToDashBoard = () => {
    if (UserType.merchant)
      return (window.location.href = `${process.env.PUBLIC_URL}/admin/dashboard`);
    if (UserType.merchantUserV1 || UserType.merchantUserV2)
      return (window.location.href = `${process.env.PUBLIC_URL}/dashboard`);
    if (UserType.userV2)
      return (window.location.href = `${process.env.PUBLIC_URL}/select-merchant`);
  };

  useEffect(() => {
    if (!props.data) return;
    if (UserType.merchantUserV1 || UserType.merchantUserV2) {
      if (!props?.data.userName) {
        setName(`${props.data.merchantName} - ${props.data.phoneNum?.slice(7, 11)} 님` || '');
      } else {
        setName(`${props.data.merchantName} - ${props.data.userName} 님` || '');
      }
      return;
    }
    if (UserType.userV2) return setName(`${props.data.phoneNum?.slice(7, 11)} 님` || '');
    if (UserType.merchant) {
      setName(props?.data?.merchantName || props.data.name);
      setMerchantCode(props.data.phoneNum || props.data.merchant_code);
      return;
    }
    if (UserType.admin) {
      setName('최고 관리자');
      return;
    }
  }, [
    props.data,
    UserType.admin,
    UserType.merchant,
    UserType.merchantUserV1,
    UserType.merchantUserV2,
    UserType.userV2,
  ]);

  useEffect(() => {
    if (!canUseChannelTalk) return;
    const channelProfile = props?.data || merchantData;
    const memberId =
      channelProfile?.merchant_code ||
      channelProfile?.phoneNum ||
      channelProfile?.id ||
      (UserType.admin ? 'letmeup-admin' : undefined);
    if (!memberId) return;

    ChannelService.boot({
      pluginKey: CHANNEL_TALK_PLUGIN_KEY,
      memberId: String(memberId),
      profile: {
        name:
          channelProfile?.name ||
          channelProfile?.merchantName ||
          (UserType.admin ? '최고 관리자' : undefined),
        mobileNumber: channelProfile?.contact || channelProfile?.phoneNum,
        merchant: channelProfile?.name || channelProfile?.merchantName,
        phoneNum: channelProfile?.contact || channelProfile?.phoneNum,
      },
    });
  }, [canUseChannelTalk, merchantData, props?.data, UserType.admin]);

  const hasAnyMultiKiosk = usingMultiKiosk || usingLegacyMultiKiosk;
  const showMerchantDoorAction =
    UserType.merchant && !(UserType.merchantUserV1 || UserType.merchantUserV2);
  const showDropdownShortcutActions = screenType !== 'desktop';
  const hasDropdownPrimaryActions =
    showMerchantDoorAction ||
    (showDropdownShortcutActions && (canUseHeaderActions || canUseChannelTalk));

  return (
    <Fragment>
      <Suspense fallback={null}>
        {isNoticeModal && (
          <HeaderNoticeModal
          isOpen={isNoticeModal}
          onClose={() => setIsNoticeModal(false)}
          onReadChange={fetchUnreadNotices}
        />
        )}
        {isOpinionModal && (
          <SendOpinionModal isOpen={isOpinionModal} onClose={() => setIsOpinionModal(false)} />
        )}
        {isDoorModal && hasAnyMultiKiosk && (
          <SocketControlModal
            show={isDoorModal}
            onClose={setIsDoorModal}
            data={usingMultiKiosk ? kioskOptions : merchantData?.multiKioskFloor}
            usingLegacyMultiKiosk={usingLegacyMultiKiosk}
            usingMultiKiosk={usingMultiKiosk}
            kioskOptions={kioskOptions}
            legacyMultiKioskFloor={legacyMultiKioskFloor}
            handleOpenDoor={handleOpenDoor}
            handleOpenDoorLegacy={handleOpenDoorLegacy}
            kind="Door"
          />
        )}
      </Suspense>
      {screenType !== 'desktop' && (
        <MobileHeader>
          <div className="mobile-left">
            <HamburgerButton
              type="button"
              className="mobile-menu-button"
              aria-label={props.isShowSide ? '메뉴 닫기' : '메뉴 열기'}
              aria-expanded={props.isShowSide}
              $open={props.isShowSide}
              onClick={() => props.setIsShowSide(!props.isShowSide)}>
              <span />
              <span />
              <span />
            </HamburgerButton>
            {screenType === 'tablet' && (
              <img className="logo" src={LogoLetmeup} alt="렛미업" onClick={moveToDashBoard} />
            )}
          </div>
          {screenType === 'mobile' && (
            <MobilePageTitle
              type="button"
              aria-label="대시보드로 이동"
              title={merchantData?.name || merchantData?.merchantName || pageTitle}
              onClick={moveToDashBoard}>
              {merchantData?.name || merchantData?.merchantName || pageTitle}
            </MobilePageTitle>
          )}
          {screenType === 'mobile' && (
            <div className="mobile-right">
              <MobileAccountButton
                type="button"
                aria-label="지점 및 계정 메뉴"
                aria-controls="merchant-account-menu"
                aria-expanded={toggleMenuEnabled}
                onClick={handleClick}>
                <img src={LogoLetmeupMark} alt="" />
              </MobileAccountButton>
            </div>
          )}
          {screenType === 'tablet' && MenuHandler}
        </MobileHeader>
      )}

      <Container>
        {screenType === 'desktop' && MenuHandler}
        {screenType === 'desktop' && (
          <DesktopPageContext aria-label="현재 페이지">
            <strong>{pageTitle}</strong>
          </DesktopPageContext>
        )}
        {screenType === 'desktop' && canUseHeaderActions && (
          <DesktopShortcutNav aria-label="상단 바로가기 메뉴">
            <DesktopShortcutButton type="button" onClick={openCustomerChat}>
              <Inbox />
              채팅
              {unreadChatCount > 0 && (
                <HeaderCount aria-label={`읽지 않은 채팅 ${unreadChatCount}개`}>
                  {unreadChatCount > 99 ? '99+' : unreadChatCount}
                </HeaderCount>
              )}
            </DesktopShortcutButton>
            <DesktopShortcutButton type="button" onClick={openNotice}>
              <Bell />
              공지사항
              {unreadNoticeCount > 0 && (
                <HeaderCount aria-label={`읽지 않은 공지 ${unreadNoticeCount}개`}>
                  {unreadNoticeCount > 99 ? '99+' : unreadNoticeCount}
                </HeaderCount>
              )}
            </DesktopShortcutButton>
            <DesktopShortcutButton type="button" onClick={openOpinion}>
              <MessageSquare />
              기능 제안
            </DesktopShortcutButton>
            {canUseChannelTalk && (
              <DesktopShortcutButton type="button" onClick={openChannelTalk}>
                <MessageCircle />
                1:1 문의
              </DesktopShortcutButton>
            )}
          </DesktopShortcutNav>
        )}
        <div className="nav-right col-8 pull-right right-header p-0">
          <ul className="nav-menus">
            <li className="profile-nav onhover-dropdown p-0">
              <ToggleMenu
                id="merchant-account-menu"
                className="merchant-account-menu"
                role="menu"
                aria-hidden={!toggleMenuEnabled}
                enabled={toggleMenuEnabled}
                $anchor={menuAnchor}>
                {screenType !== 'desktop' && (
                  <ResponseMerchantName>
                    <div>
                      <img className="user" src={User} alt="user" />
                    </div>{' '}
                    <div>
                      {name ? name : ''}
                      {merchantCode && (
                        <>
                          <small>({merchantCode})</small>
                        </>
                      )}
                    </div>
                  </ResponseMerchantName>
                )}
                <div className="list-wrapper">
                  {showDropdownShortcutActions && canUseHeaderActions && (
                    <MenuListItem onClick={openCustomerChat}>
                      <Inbox size={18} color="#04c9db" />
                      <MenuLabel>
                        <span>채팅</span>
                        {unreadChatCount > 0 && (
                          <HeaderCount aria-label={`읽지 않은 채팅 ${unreadChatCount}개`}>
                            {unreadChatCount > 99 ? '99+' : unreadChatCount}
                          </HeaderCount>
                        )}
                      </MenuLabel>
                    </MenuListItem>
                  )}
                  {showMerchantDoorAction &&
                    (props && !hasAnyMultiKiosk ? (
                      <MenuListItem onClick={() => openDoor(props.mid)}>
                        <img src={OpenDoor} alt="OpenDoor" />
                        <span>문 열기</span>
                      </MenuListItem>
                    ) : (
                      <MenuListItem onClick={() => setIsDoorModal(true)}>
                        <img src={OpenDoor} alt="OpenDoor" />
                        <span>멀티 문열기</span>
                      </MenuListItem>
                    ))}
                  {showDropdownShortcutActions && canUseHeaderActions && (
                    <MenuListItem onClick={openNotice}>
                      <Bell size={18} color="#04c9db" />
                      <MenuLabel>
                        <span>공지사항</span>
                        {unreadNoticeCount > 0 && (
                          <HeaderCount aria-label={`읽지 않은 공지 ${unreadNoticeCount}개`}>
                            {unreadNoticeCount > 99 ? '99+' : unreadNoticeCount}
                          </HeaderCount>
                        )}
                      </MenuLabel>
                    </MenuListItem>
                  )}
                  {showDropdownShortcutActions && canUseHeaderActions && (
                    <MenuListItem onClick={openOpinion}>
                      <MessageSquare size={18} color="#04c9db" />
                      <span>기능 제안</span>
                    </MenuListItem>
                  )}
                  {showDropdownShortcutActions && canUseChannelTalk && (
                    <MenuListItem onClick={openChannelTalk}>
                      <MessageCircle size={18} color="#04c9db" />
                      <span>1:1 문의</span>
                    </MenuListItem>
                  )}
                  {hasDropdownPrimaryActions && <Divider />}
                  <MenuListItem onClick={handleLogout}>
                    <img src={LogOut} alt="logout" />
                    <span>로그아웃</span>
                  </MenuListItem>
                  {(UserType.merchantUserV2 || UserType.adminMerchant) && (
                    <>
                      <Divider />
                      <MenuListItem onClick={goToMerchantList}>
                        <img src={List} alt="list" />
                        <span>지점 리스트</span>
                      </MenuListItem>
                    </>
                  )}
                </div>
              </ToggleMenu>
              {/* <ul className="profile-dropdown onhover-show-div">
                {isMobile && (
                  <ResponseMerchantName>
                    <div>
                      <img className="user" src={User} alt="user" />
                    </div>{' '}
                    <div>
                      {name ? name : ''}
                      {merchantCode && (
                        <>
                          <small>({merchantCode})</small>
                        </>
                      )}
                    </div>
                  </ResponseMerchantName>
                )}
                {UserType.merchant &&
                  !(UserType.merchantUserV1 || UserType.merchantUserV2) &&
                  (props && !props.data.multiKioskFloor ? (
                    <li onClick={() => openDoor(props?.mid)}>
                      <img src={OpenDoor} alt="OpenDoor" />
                      <span>문 열기</span>
                    </li>
                  ) : (
                    <li onClick={() => setIsDoorModal(true)}>
                      <img src={OpenDoor} alt="OpenDoor" />
                      <span>멀티 문열기</span>
                    </li>
                  ))}
                <hr />
                <li onClick={handleLogout}>
                  <img src={LogOut} alt="logout" />
                  <span>로그아웃</span>
                </li>
                <hr />
                {(UserType.merchantUserV2 || UserType.adminMerchant) && (
                  <li onClick={goToMerchantList}>
                    <img src={List} alt="list" />
                    <span>지점 리스트</span>
                  </li>
                )}
              </ul> */}
            </li>
          </ul>
        </div>
      </Container>
    </Fragment>
  );
};
export default Rightbar;
